import * as React from "react";

import {
  doesElementContainFocus,
  EventGroup,
  focusFirstChild,
} from "@fluentui/react";

import { AppButtonProps } from "@1js/acui-button";
import { dataUniqueId, UILocation } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";

import { RibbonButton } from "../../Controls/RibbonButton";
import { hasPinnedCombobox } from "../../utilities/hasPinnedControl";
import { animatePropsChanges } from "../PropsAnimator/PropsAnimator";
import { shouldShadowRenderMenu } from "../RibbonControlGroup";
import { RibbonControlGroupBase } from "../RibbonControlGroup/RibbonControlGroup";
import {
  ControlGroupAnimationProps,
  getControlGroupLayoutAnimationOptions,
} from "../RibbonControlGroup/RibbonControlGroupAnimation";
import { deriveMLRBottomRowKeytipProps } from "./deriveMLRKeytipProps";
import { LayoutRenderer } from "./layoutRenderer";
import { getRibbonControlGroupMLRClassNames } from "./RibbonControlGroupMLR.classNames";
import { RibbonControlGroupMLRProps } from "./RibbonControlGroupMLR.Props";
import { getGroupLauncherStyles } from "./RibbonControlGroupMLR.styles";
import { MLRControlGroupLabelId } from "../RibbonControlGroup/ribbonControlGroupUtilities";

class RibbonControlGroupMLRBase
  extends React.PureComponent<
    RibbonControlGroupMLRProps & ControlGroupAnimationProps,
    {}
  >
  implements RibbonControlGroupBase
{
  private container = React.createRef<HTMLDivElement>();
  private events: EventGroup;

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  public constructor(props: RibbonControlGroupMLRProps) {
    super(props);
    this.events = new EventGroup(this);
  }

  public componentDidMount(): void {
    if (this.container.current && this.props.onFocusIn) {
      this.events.on(
        this.container.current,
        "focusin",
        this.props.onFocusIn,
        true /* useCapture */
      );
    }
  }

  public componentWillUnmount(): void {
    this.events.off();
  }

  public render() {
    const classNames = getRibbonControlGroupMLRClassNames(
      this.props.initialWidth,
      this.props.finalWidth
    );
    const { id, hideGroupLabel, groupLabel, fastDisabled } = this.props;
    const groupId = `${id}-group`;
    const groupLabelId = MLRControlGroupLabelId(id);

    return (
      <div
        className={`${classNames.root}`}
        ref={this.container}
        data-unique-id={dataUniqueId(this.props)}
        id={groupId}
        role="group"
        aria-labelledby={!hideGroupLabel ? groupLabelId : undefined}
        aria-label={hideGroupLabel ? groupLabel : undefined}
      >
        <div className={classNames.container}>
          <div className={`${classNames.layoutWrapper} layoutWrapper`}>
            <LayoutRenderer
              layout={this.props.layout}
              controlProps={this.getControlsWithKey(fastDisabled)}
              controlRendererFunction={this.props.controlRendererFunction}
              animate={!!this.props.animate}
              layoutChanges={this.props.layoutChanges}
              dismissMenu={this.props.dismissMenu}
              isRibbonSpanning={this.props.isRibbonSpanning}
              spanningComponentId={this.props.spanningComponentId}
              spanningOffset={this.props.spanningOffset}
            />
          </div>
          <div className={classNames.groupLabelContainer}>
            {!hideGroupLabel && (
              <label
                className={classNames.groupLabel}
                htmlFor={groupId}
                id={groupLabelId}
              >
                {groupLabel}
              </label>
            )}
            {this.props.groupLauncher && (
              <this.renderGroupLauncher {...this.props.groupLauncher} />
            )}
          </div>
        </div>
      </div>
    );
  }

  private renderGroupLauncher = React.memo(
    (buttonProps: AppButtonProps): JSX.Element => {
      return (
        <RibbonButton
          type="AppButtonProps"
          label={buttonProps.label}
          id={buttonProps.id}
          onExecute={buttonProps.onExecute}
          icon={buttonProps.icon}
          variant="groupLauncher"
          keytipProps={deriveMLRBottomRowKeytipProps(buttonProps.keytipProps)}
          hideLabel={true}
          styles={getGroupLauncherStyles()}
          disabled={buttonProps.disabled}
          location={UILocation.MultilineRibbon}
        />
      );
    }
  );

  private getControlsWithKey = (
    fastDisabled?: boolean | (() => boolean | undefined)
  ) => {
    const topLevelControlsWithKey = this.props.controls.map(
      (item, index, controls) => {
        return {
          ...item,
          key: item.id,
          fastDisabledCallback:
            typeof fastDisabled === "function" ? fastDisabled : undefined,
          shadowRenderMenu: shouldShadowRenderMenu(
            `shouldShadowRenderFn-${item.id}${item.location || ""}-MLR`,
            typeof fastDisabled === "function" ? fastDisabled() : undefined,
            (item as any).shadowRenderMenu
          ),
          ...hasPinnedCombobox(controls, index),
        };
      }
    );
    return topLevelControlsWithKey;
  };

  public hasContainer(): boolean {
    return !!this.container.current;
  }

  public focus(): void {
    if (this.container.current) {
      // Focus the first focusable element in the control group
      focusFirstChild(this.container.current);
    }
  }

  public containsFocus(): boolean {
    if (this.hasContainer()) {
      return doesElementContainFocus(this.container.current as HTMLElement);
    }

    return false;
  }
}

export const RibbonControlGroupMLR = animatePropsChanges(
  getControlGroupLayoutAnimationOptions<RibbonControlGroupMLRProps>()
)(RibbonControlGroupMLRBase);
export type RibbonControlGroupMLR = typeof RibbonControlGroupMLR.prototype;
